import { EhrConnector, MeProfile } from "@elation/api-client/src/stitch-web";
import { datadogRum } from "./datadog";

type IframeLoginMethod = `iframe_${EhrConnector}`;

export type LoginMethod =
  | "okta_web"
  | "okta_chrome"
  | "email"
  | IframeLoginMethod;

/**
 * Builds the iframe-flavored `LoginMethod` for a given EHR connector
 * (e.g. `iframe_elation`, `iframe_athenahealth`, `iframe_eclinicalworks`).
 * Stays in sync with the `EhrConnector` enum so new connectors are picked
 * up automatically.
 */
export function iframeLoginMethod(ehr: EhrConnector): IframeLoginMethod {
  return `iframe_${ehr}` as IframeLoginMethod;
}

/**
 * Identifies the authenticated user on the active Datadog RUM session and emits
 * a `user_logged_in` custom action.
 *
 * Should be called from each login completion path (Okta web callback,
 * email/password `onSuccess`, Chrome extension Okta exchange) immediately
 * after `/me` resolves, so RUM has a canonical user + practice + login method
 * for the session.
 *
 * Safe to call when RUM did not initialize (missing token / mocking / tests):
 * `datadogRum` no-ops in that case.
 */
export function trackUserLoggedIn(
  loginResponse: MeProfile,
  method: LoginMethod,
): void {
  const { user, practice, type } = loginResponse;

  const fullName = [user.firstName, user.lastName]
    .filter((part): part is string => Boolean(part))
    .join(" ")
    .trim();

  datadogRum.setUser({
    id: String(user.id),
    ...(user.email ? { email: user.email } : {}),
    ...(fullName ? { name: fullName } : {}),
    practice_id: practice.id,
    user_type: type,
  });

  datadogRum.addAction("user_logged_in", {
    method,
    user_id: user.id,
    user_type: type,
    practice_id: practice.id,
    practice_name: practice?.name,
  });
}
